import { useEffect, useState } from 'react';

import { getIdFromUrlPath } from '@shared/helpers';
import { useGetUnreadCommentsPolling } from '@widgets/Comments/api/hooks';
import {
    COMMENTS_CLOSED_EVENT_NAME,
    COMMENTS_OPEN_EVENT_NAME,
    COMMENTS_READ_EVENT_NAME,
    DEFAULT_CLOSED_INTERVAL_MS,
} from '@widgets/Comments/consts';
import { applyUnreadCommentsStateToDom } from '@widgets/Comments/lib/polling';
import { useCommentTypeFromActiveTab } from '@widgets/Comments/model';

interface ICommentsOpenEventDetail {
    positionGuid?: string;
}

export const CommentsPollingHost = (): null => {
    const closedIntervalMs = DEFAULT_CLOSED_INTERVAL_MS;
    const estimateId = getIdFromUrlPath('estimate');
    const [isCommentsOpen, setIsCommentsOpen] = useState(false);
    const commentType = useCommentTypeFromActiveTab();
    const [accumulatedUnreadPositionGuids, setAccumulatedUnreadPositionGuids] = useState<string[]>([]);

    useEffect(() => {
        const handleOpen = (event: Event): void => {
            const customEvent = event as CustomEvent<ICommentsOpenEventDetail>;

            if (!customEvent.detail?.positionGuid) {
                return;
            }

            setIsCommentsOpen(true);
        };

        const handleClosed = (): void => {
            setIsCommentsOpen(false);
        };
        const handleRead = (event: Event): void => {
            const customEvent = event as CustomEvent<ICommentsOpenEventDetail>;
            const readPositionGuid = String(customEvent.detail?.positionGuid || '').trim();

            if (!readPositionGuid) {
                return;
            }

            setAccumulatedUnreadPositionGuids((previousGuids) =>
                previousGuids.filter((guid) => guid !== readPositionGuid)
            );
        };

        window.addEventListener(COMMENTS_OPEN_EVENT_NAME, handleOpen as EventListener);
        window.addEventListener(COMMENTS_CLOSED_EVENT_NAME, handleClosed as EventListener);
        window.addEventListener(COMMENTS_READ_EVENT_NAME, handleRead as EventListener);

        return () => {
            window.removeEventListener(COMMENTS_OPEN_EVENT_NAME, handleOpen as EventListener);
            window.removeEventListener(COMMENTS_CLOSED_EVENT_NAME, handleClosed as EventListener);
            window.removeEventListener(COMMENTS_READ_EVENT_NAME, handleRead as EventListener);
        };
    }, []);

    const { data: unreadPositionGuids = [], dataUpdatedAt } = useGetUnreadCommentsPolling({
        estimateId,
        isCommentsOpen,
        closedIntervalMs,
        commentType,
    });

    useEffect(() => {
        if (!dataUpdatedAt) {
            return;
        }

        setAccumulatedUnreadPositionGuids((previousGuids) =>
            Array.from(new Set([...previousGuids, ...unreadPositionGuids]))
        );
    }, [dataUpdatedAt, unreadPositionGuids]);

    useEffect(() => {
        applyUnreadCommentsStateToDom(accumulatedUnreadPositionGuids);
    }, [accumulatedUnreadPositionGuids]);

    return null;
};
